import { useEffect, useMemo, useRef, useState } from 'react';

import {
  AssetDTO,
  AssetSelectFormElementAnswerSchema,
  AssetSelectFormElement as AssetSelectFormElementType,
  EntityRefUtil,
  RecordState,
} from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { AsyncSelect } from '@/components/ui/async-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MULTI_SELECT_STYLES } from '@/components/ui/multi-select';
import { DoubleClickInput } from '@/components/ui/select';
import { getAssetsQueryOptions } from '@/queries/assets';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useIsAWA } from '@/utils/awa';

import { ConfigLabel } from '../config-label';
import { FormFieldError } from '../form-field-error';

export const AssetSelectFormElement = ({
  element,
  value,
  onChange,
  onChangeFull,
  onBlur,
  defaultAssetId,
  readonly,
  error,
}: {
  element: AssetSelectFormElementType;
  value?: z.infer<typeof AssetSelectFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof AssetSelectFormElementAnswerSchema>['value']) => void;
  onChangeFull?: (value: AssetDTO | AssetDTO[] | null) => void;
  onBlur?: () => void;
  defaultAssetId?: string;
  readonly?: boolean;
  error?: string;
}) => {
  const { isAWA } = useIsAWA();

  const ref = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { data: assets = [] } = useQuery(
    getAssetsQueryOptions({
      limit: 100,
      isAWA,
      filters: {
        state: RecordState.Active,
        fields: {
          ...(defaultAssetId ? { id: [defaultAssetId] } : {}),
        },
      },
    }),
  );
  const options = useMemo(
    () =>
      assets.map((asset) => ({
        value: asset.id,
        label: EntityRefUtil.createAssetLabel(asset),
        asset,
      })),
    [assets],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const className = target?.getAttribute('class') || '';
      const clickedInsideRef = className.includes(element.id);
      if (clickedInsideRef) {
        return;
      }

      setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [element.id]);

  return (
    <div className="space-y-1">
      <div className="space-y-2">
        <div ref={ref}>
          <Label required={element.required}>{element.label}</Label>
          <AsyncSelect<{ value: string; label: string; asset: AssetDTO }>
            cacheOptions
            defaultOptions={options}
            blurInputOnSelect
            value={
              defaultAssetId
                ? options.find((opt) => opt.value === defaultAssetId) || null
                : element.config?.multiple
                  ? options.filter((opt) => Array.isArray(value) && value.includes(opt.value))
                  : // We are purposely setting this to undefined if there is no default asset ID to let AsyncSelect manage its own state
                    undefined
            }
            classNamePrefix={element.id}
            loadOptions={async (input: string) => {
              const { data } = await (
                await (isAWA ? AWAApiV2Service : ApiService).GetAssets({
                  limit: 100,
                  filters: {
                    state: RecordState.Active,
                    search: input,
                  },
                })
              ).json();
              if (!data) return [];

              return data.map((asset) => ({
                value: asset.id,
                label: EntityRefUtil.createAssetLabel(asset),
                asset,
              }));
            }}
            onChange={(newValue) => {
              if (element.config?.multiple) {
                onChange(Array.isArray(newValue) ? newValue.map((v) => v.value) : []);
                onChangeFull?.(Array.isArray(newValue) ? newValue.map((v) => v.asset) : null);
              } else {
                onChange(newValue?.value || null);
                onChangeFull?.(newValue?.asset || null);
              }
            }}
            onBlur={onBlur}
            menuIsOpen={isMenuOpen}
            components={{
              Input: (props) => <DoubleClickInput {...props} setIsMenuOpen={setIsMenuOpen} />,
            }}
            styles={{
              control: (state) => ({
                ...state,
                borderColor: isMenuOpen ? 'rgb(var(--default-blue))' : 'rgb(var(--grey-3))',
                opacity: '1',
                backgroundColor: readonly ? 'rgb(var(--grey-1))' : state.backgroundColor,
              }),
            }}
            placeholder="Select Asset..."
            isClearable
            isDisabled={readonly}
            // @ts-expect-error - React Select has PITA types for this
            isMulti={element.config?.multiple ?? false}
            // @ts-expect-error - React Select has PITA types for this
            classNames={MULTI_SELECT_STYLES(!!error)}
          />
        </div>
      </div>

      <FormFieldError error={error} />
    </div>
  );
};

export function AssetSelectFormElementMock({ element }: { element: AssetSelectFormElementType }) {
  const [value, setValue] = useState<string | string[] | null>(element.config?.multiple ? [] : null);
  const { isAWA } = useIsAWA();

  const ref = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { data: assets = [] } = useQuery(
    getAssetsQueryOptions({
      limit: 100,
      isAWA,
      filters: {
        state: RecordState.Active,
      },
    }),
  );
  const options = useMemo(
    () =>
      assets.map((asset) => ({
        value: asset.id,
        label: EntityRefUtil.createAssetLabel(asset),
      })),
    [assets],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const className = target?.getAttribute('class') || '';
      const clickedInsideRef = className.includes(element.id);
      if (clickedInsideRef) {
        return;
      }

      setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [element.id]);

  return (
    <div ref={ref}>
      <Label required={element.required}>{element.label}</Label>
      <AsyncSelect<{ value: string; label: string }>
        cacheOptions
        defaultOptions={options}
        blurInputOnSelect
        menuIsOpen={isMenuOpen}
        value={options.find((opt) => opt.value === value) || null}
        components={{
          Input: (props) => <DoubleClickInput {...props} setIsMenuOpen={setIsMenuOpen} />,
        }}
        styles={{
          control: (state) => ({
            ...state,
            borderColor: isMenuOpen ? 'rgb(var(--default-blue))' : 'rgb(var(--grey-3))',
          }),
        }}
        classNamePrefix={element.id}
        loadOptions={async (input: string) => {
          const { data } = await (
            await (isAWA ? AWAApiV2Service : ApiService).GetAssets({
              limit: 100,
              filters: {
                state: RecordState.Active,
                search: input,
              },
            })
          ).json();
          if (!data) return [];

          return data.map((asset) => ({
            value: asset.id,
            label: EntityRefUtil.createAssetLabel(asset),
          }));
        }}
        onChange={(newValue) => {
          if (element.config?.multiple) {
            setValue(Array.isArray(newValue) ? newValue.map((v) => v.value) : []);
          } else {
            setValue(newValue?.value || null);
          }
        }}
        placeholder="Select Asset..."
        isClearable
        // @ts-expect-error - React Select has PITA types for this
        isMulti={element.config?.multiple ?? false}
        // @ts-expect-error - React Select has PITA types for this
        classNames={MULTI_SELECT_STYLES()}
      />
    </div>
  );
}

export function AssetSelectFormElementConfig({
  element,
  onChange,
}: {
  element: AssetSelectFormElementType;
  onChange?: (updates: Partial<AssetSelectFormElementType>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox
          checked={element.config?.multiple ?? false}
          onCheckedChange={(checked) =>
            onChange?.({
              config: {
                ...element.config,
                multiple: !!checked,
              },
            })
          }
        />
        <span>Allow multiple selection</span>
      </ConfigLabel>
    </div>
  );
}
